import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  Briefcase,
  DollarSign,
  FileText,
  LogOut,
  Building2,
  MoreVertical,
  Users,
  UserCog,
  User,
  LayoutDashboard,
  Network,
  Telescope,
  GraduationCap,
  Receipt,
  Files,
  Route,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';

interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  requirePermission?: 'canManageUsers' | 'canViewCRM' | 'canViewProjects';
}

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { setOpenMobile, isMobile } = useSidebar();
  const permissions = usePermissions();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  // Build nav items based on permissions - grouped by section
  const workspaceItems: NavItem[] = [];
  const adminItems: NavItem[] = [];

  // Determine if user should see personal dashboard vs company portfolio
  // Team leads are team members with the pipelines bolted on, so they get the
  // same personal dashboard rather than the company portfolio.
  const showPersonalDashboard = permissions.isExternalPartner ||
    ((permissions.isTeamMember || permissions.isTeamLead) && !permissions.canViewAllProjects);

  // Only show main nav items if user is not pending
  if (!permissions.isPending) {
    // Dashboard view based on role
    if (permissions.canViewProjects) {
      if (showPersonalDashboard) {
        // External partners and team members see personal dashboard
        workspaceItems.push({
          title: 'My Dashboard',
          url: '/my-dashboard',
          icon: LayoutDashboard,
        });
      } else {
        // Admins and owners see company portfolio
        workspaceItems.push({
          title: 'Portfolio',
          url: '/',
          icon: Briefcase,
        });
      }
    }

    // Pipeline (CRM) - visible to those who can view CRM (not external partners)
    if (permissions.canViewCRM) {
      workspaceItems.push({
        title: 'Pipeline',
        url: '/crm',
        icon: Users,
      });
    }

    // Payments - visible to super_admin and owners (under Projects workspace)
    if (permissions.isSuperAdmin || permissions.isOwner) {
      workspaceItems.push({
        title: 'Payments',
        url: '/projects/payments',
        icon: DollarSign,
      });
      workspaceItems.push({
        title: 'Billing',
        url: '/billing',
        icon: Receipt,
      });
    }

    // Weekly Reports - visible to super_admin and owners
    if (permissions.isSuperAdmin || permissions.isOwner) {
      workspaceItems.push({
        title: 'Weekly Reports',
        url: '/reports',
        icon: FileText,
      });
    }

    // Documents (shared pages) - visible to super_admin and owners
    if (permissions.isSuperAdmin || permissions.isOwner) {
      workspaceItems.push({
        title: 'Documents',
        url: '/documents',
        icon: Files,
      });
    }

    // Connects - visible to super_admin and owners
    if (permissions.isSuperAdmin || permissions.isOwner) {
      workspaceItems.push({
        title: 'Connects',
        url: '/connects',
        icon: Network,
      });
    }

    // Prospects + Journey - visible to super_admin, owners, and pipeline managers
    if (permissions.canManagePipeline) {
      workspaceItems.push({
        title: 'Prospects',
        url: '/prospects',
        icon: Telescope,
      });
      workspaceItems.push({
        title: 'Journey',
        url: '/journey',
        icon: Route,
      });
    }

    // Intern Admin Console - super_admin only (Admin section)
    if (permissions.canManageInterns) {
      adminItems.push({
        title: 'Interns',
        url: '/interns',
        icon: GraduationCap,
      });
    }

    // Team Management - super_admin only (Admin section)
    if (permissions.canManageUsers) {
      adminItems.push({
        title: 'Team & Permissions',
        url: '/team',
        icon: UserCog,
      });
    }
  }

  const handleNavigation = (url: string) => {
    navigate(url);
    setOpenMobile(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Get display name from profile or fallback to email
  const displayName = permissions.userProfile?.display_name || user?.email?.split('@')[0] || 'User';

  // Determine the home route based on role
  const homeRoute = permissions.isPipelineManager
    ? '/prospects'
    : showPersonalDashboard ? '/my-dashboard' : '/';

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="cursor-pointer"
              onClick={() => handleNavigation(homeRoute)}
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <Building2 className="size-4" />
              </div>
              <div className="flex flex-col gap-0.5 leading-none">
                <span className="text-[#B8963E] font-serif tracking-tight font-semibold">XYZ Strategy</span>
                <span className="text-xs text-muted-foreground">Operations & Finance</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* Workspaces Section */}
        {workspaceItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Workspaces</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {workspaceItems.map((item) => {
                  const isActive = location.pathname === item.url ||
                    (item.url !== '/' && location.pathname.startsWith(item.url));
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        onClick={() => handleNavigation(item.url)}
                        isActive={isActive}
                        tooltip={item.title}
                      >
                        <item.icon className="size-4" />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Admin Section */}
        {adminItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {adminItems.map((item) => {
                  const isActive = location.pathname === item.url ||
                    (item.url !== '/' && location.pathname.startsWith(item.url));
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        onClick={() => handleNavigation(item.url)}
                        isActive={isActive}
                        tooltip={item.title}
                      >
                        <item.icon className="size-4" />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="relative">
              <SidebarMenuButton
                size="lg"
                className={profileMenuOpen ? "bg-sidebar-accent text-sidebar-accent-foreground" : ""}
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              >
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                    {displayName.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {displayName}
                  </span>
                  <span className="text-muted-foreground truncate text-xs">
                    {user?.email || 'Not signed in'}
                  </span>
                </div>
                <MoreVertical className="ml-auto size-4" />
              </SidebarMenuButton>
              {profileMenuOpen && (
                <div className={`absolute ${isMobile ? 'bottom-full mb-2 left-0' : 'left-full ml-2 bottom-0'} w-56 bg-popover text-popover-foreground rounded-lg border shadow-md z-50`}>
                  <div className="p-0 font-normal">
                    <div className="flex items-center gap-2 px-2 py-1.5 text-left text-sm">
                      <Avatar className="h-8 w-8 rounded-lg">
                        <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                          {displayName.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate font-medium">
                          {displayName}
                        </span>
                        <span className="text-muted-foreground truncate text-xs">
                          {user?.email || 'Not signed in'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Separator />
                  <button
                    className="flex w-full items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded-sm"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      handleNavigation('/profile');
                    }}
                  >
                    <User className="size-4" />
                    Profile
                  </button>
                  <Separator />
                  <button
                    className="flex w-full items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded-sm"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      handleSignOut();
                    }}
                  >
                    <LogOut className="size-4" />
                    Log out
                  </button>
                </div>
              )}
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
